<script setup>
import { ref, computed, watch } from 'vue'
import SiteAdminService, { featuredSourceSlides } from '@/services/SiteAdminService'
import { itemRef, paidMappingChosen, plansEmptiedBy } from '@/Utils/siteAccess'
import { castrSourceNoun } from '@/Utils/siteContentKind'
import { featuredSourceMessage } from '@/Utils/siteFeatured'
import { notifySite } from '@/Utils/siteToast'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'
import { toLocalInput, fromLocalInput, addMinutes, minutesBetween, localTimeZone, fmtDateTime, DURATION_OPTIONS } from '@/Utils/siteSchedule'
import { broadcastScheduleConflict, broadcastConflictMessage, serviceTimeNotice } from '@/Utils/siteBroadcastSchedule'
import ModalBox from '@/components/modals/ModalBox.vue'
import StreamSourceDropdown from '@/components/ui/StreamSourceDropdown.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormTextarea from '@/components/Atoms/FormTextarea.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import FormCheckbox from '@/components/Atoms/FormCheckbox.vue'
import Alert from '@/components/Atoms/Alert.vue'
import SiteImageField from './SiteImageField.vue'
import SiteAccessSelect from './SiteAccessSelect.vue'
import SiteContentCardPreview from './SiteContentCardPreview.vue'

// Add / edit a content item in a right-hand drawer: Source → Details → Access
// → Schedule with a live card preview beside the form. Source is a Castr
// stream / VOD file / playlist (castr resolves the playback URL server-side
// from the ref). External URLs are no longer offered (Trello qG88dqXo): an
// older item that has one keeps it until the owner picks a Castr source, and
// its link is never re-sent on save. Access is per item only
// (access model v2): Public / Login required / Paid, nothing inherited.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	categories: {
		type: Array,
		default: () => [],
	},
	plans: {
		type: Array,
		default: () => [],
	},
	// Stripe Connect gate for the "Paid" access level
	canMonetize: {
		type: Boolean,
		default: true,
	},
	stripe: {
		type: Object,
		default: null,
	},
	/**
	 * Effective site setting `playerOpenImmediately` (Theme › Player; runtime
	 * default true). A new item's "Open the player immediately" toggle starts
	 * from it; the saved per-item value always wins when editing.
	 */
	playerOpenImmediately: {
		type: Boolean,
		default: true,
	},
	/** Theme › Player badge text for paid items (`useSiteAdmin().premiumBadgeLabel`), shown on the card preview. */
	paidBadgeLabel: {
		type: String,
		default: 'Premium',
	},
	/**
	 * Broadcast sites only: `{ serviceTimes, timeZone, liveItemId }` — the
	 * site's service times and zone, and the main stream's id (`mainLiveItemId`,
	 * 0 = none yet). Categories are hidden (the template has no category nav)
	 * and a live item's schedule is checked against the service times / the
	 * item's other Calendar entries that also drive the stage countdown.
	 */
	broadcast: {
		type: Object,
		default: null,
	},
})
const emit = defineEmits(['saved', 'create-plan', 'stripe-connected', 'calendar-changed'])

const isVisible = ref(false)
const saving = ref(false)
const error = ref(null)
// Set with `error` when the runtime refused to hide the item (409 featured_source): links to Featured.
const errorLinksFeatured = ref(false)
const editing = ref(null)
const sourceTab = ref('castr') // castr | external (legacy item, read-only)
const castrSource = ref(null) // stream object from the picker
const resolving = ref(false)
const resolved = ref(null) // castr-playback preview
const form = ref(blank())
const planIds = ref([])
// Paid without ticked plans is only valid when the owner says so explicitly.
const allAccessOnly = ref(false)

function blank() {
	return {
		title: '',
		description: '',
		poster: '',
		categoryIds: [],
		access: 'public',
		published: true,
		autoplay: false,
		castr_ref: null,
		external_url: '',
		// Schedule (#20): datetime-local strings in the browser's zone.
		scheduledStart: '',
		availableFrom: '',
		availableUntil: '',
		calendar: { enabled: false, durationMinutes: 60, eventId: null },
	}
}

// --- Schedule ---------------------------------------------------------------
const timeZone = localTimeZone()
const scheduledStartIso = computed(() => fromLocalInput(form.value.scheduledStart))
const availableFromIso = computed(() => fromLocalInput(form.value.availableFrom))
const availableUntilIso = computed(() => fromLocalInput(form.value.availableUntil))
// The site only lists an item when both bounds are empty or both bracket "now",
// so a half-set window would hide it for good.
const availabilityError = computed(() => {
	const from = form.value.availableFrom.trim()
	const until = form.value.availableUntil.trim()
	if (!from && !until) return ''
	if (from && !availableFromIso.value) return 'Enter a valid "Available from" date and time.'
	if (until && !availableUntilIso.value) return 'Enter a valid "Available until" date and time.'
	if (!from || !until) return 'Set both "Available from" and "Available until", or leave both empty.'
	if (new Date(availableUntilIso.value) <= new Date(availableFromIso.value)) return '"Available until" must be after "Available from".'
	return ''
})
const scheduleError = computed(() => (form.value.scheduledStart.trim() && !scheduledStartIso.value ? 'Enter a valid scheduled start.' : ''))
const calendarEndIso = computed(() => (scheduledStartIso.value ? addMinutes(scheduledStartIso.value, form.value.calendar.durationMinutes) : null))
const calendarNote = computed(() => {
	if (!scheduledStartIso.value) return 'Needs a scheduled start.'
	if (!form.value.calendar.enabled) return form.value.calendar.eventId ? 'Saving removes this item from the calendar.' : ''
	return `Listed ${fmtDateTime(scheduledStartIso.value)} – ${fmtDateTime(calendarEndIso.value)} as “${form.value.title.trim() || 'Untitled'}”.`
})
// Untick automatically when the start is cleared; the entry is then deleted on save.
watch(scheduledStartIso, (iso) => { if (!iso) form.value.calendar.enabled = false })

// Every Calendar entry of the edited item ({ id, start }); the one the
// checkbox manages follows the scheduled start, the others are checked below.
const itemEvents = ref([])

/** Prefill the calendar checkbox from the item's existing event (newest first if several). */
async function loadCalendarEvent(item) {
	try {
		const res = await SiteAdminService.listEvents(props.siteId, { contentId: item.id, limit: 20 })
		if (!isVisible.value || editing.value?.id !== item.id) return
		const events = (res?.items || []).slice().sort((a, b) => b.id - a.id)
		itemEvents.value = events.map((e) => ({ id: e.id, start: e.start }))
		const event = events[0]
		if (!event) return
		form.value.calendar = {
			enabled: true,
			durationMinutes: minutesBetween(event.start, event.end, 60),
			eventId: event.id,
		}
		// The event is the source of truth for when the item is listed.
		if (!form.value.scheduledStart && event.start) form.value.scheduledStart = toLocalInput(event.start)
	} catch {
		// the schedule block still works without the prefill; saving creates a fresh entry
	}
}

/**
 * Bring the calendar entry in line with the form after the item is saved:
 * create / update it while the box is ticked, delete it when unticked.
 * @returns {string|null} a message when the calendar could not be updated
 */
async function syncCalendarEvent(item, title) {
	const { enabled, durationMinutes, eventId } = form.value.calendar
	const start = scheduledStartIso.value
	try {
		if (enabled && start && item?.id) {
			const body = { contentId: item.id, title, start, end: addMinutes(start, durationMinutes) }
			if (eventId) await SiteAdminService.updateEvent(props.siteId, eventId, body)
			else await SiteAdminService.createEvent(props.siteId, body)
			emit('calendar-changed')
		} else if (eventId) {
			await SiteAdminService.deleteEvent(props.siteId, eventId)
			emit('calendar-changed')
		}
		return null
	} catch (err) {
		return err.message || 'unknown error'
	}
}

const selectedCategories = computed(() => form.value.categoryIds.map((id) => props.categories.find((c) => String(c.id) === String(id))).filter(Boolean))

const hasSource = computed(() => (sourceTab.value === 'castr'
	? Boolean(form.value.castr_ref)
	: form.value.external_url.trim().length > 0))

// Poster prefill (Govinda's rule): only an image the owner set in Castr or one
// Castr generated for the video file. Never the live snapshot — it only renders
// while the stream is live and left sites with broken posters — so anything on
// dvr.castr.io / preview.castr.io is dropped even when an older castr returns
// it. With nothing to prefill the field stays empty and upload is the next step.
const PREFILL_SOURCES = {
	player_poster: 'Player poster set in Castr',
	vod_meta_thumbnail: 'Video thumbnail from Castr',
	vod_generated_poster: 'Video thumbnail from Castr',
}
const SNAPSHOT_HOSTS = /(^|\.)(dvr|preview)\.castr\.io$/i
function isSnapshotUrl(url) {
	try {
		return SNAPSHOT_HOSTS.test(new URL(String(url)).hostname)
	} catch {
		return false
	}
}
/** The poster URL a castr-playback preview lets us prefill, or ''. */
function prefillPoster(res) {
	const url = res?.thumbnail_url
	if (!url || !PREFILL_SOURCES[res.poster_source] || isSnapshotUrl(url)) return ''
	return String(url)
}
const prefilled = ref(null) // { url, label } of the Castr image put in the field
/**
 * Put the Castr image in the poster field unless the owner already has one
 * there; a previous prefill counts as ours and follows the new source.
 */
function applyPrefill(res) {
	const url = prefillPoster(res)
	if (!form.value.poster || form.value.poster === prefilled.value?.url) form.value.poster = url
	prefilled.value = url ? { url, label: PREFILL_SOURCES[res.poster_source] } : null
}
function dropPrefill() {
	if (prefilled.value && form.value.poster === prefilled.value.url) form.value.poster = ''
	prefilled.value = null
}
const posterNote = computed(() => {
	if (sourceTab.value !== 'castr' || !form.value.castr_ref || resolving.value) return ''
	if (form.value.poster) return form.value.poster === prefilled.value?.url ? `${prefilled.value.label} — upload your own to replace it.` : ''
	if (prefilled.value) return 'Left empty, the site falls back to the Castr thumbnail.'
	return 'Castr has no thumbnail for this source — upload one.'
})
const accessReady = computed(() => paidMappingChosen(form.value.access, planIds.value, allAccessOnly.value))
const canSubmit = computed(() => form.value.title.trim().length > 0 && hasSource.value && !saving.value && !resolving.value
	&& !availabilityError.value && !scheduleError.value && accessReady.value)
// Paid → Public / Login required drops the item from every plan that unlocked it.
const clearsMapping = computed(() => Boolean(editing.value) && (editing.value.access || 'public') === 'paid' && form.value.access !== 'paid')

const refsForThis = computed(() => (editing.value ? [itemRef(editing.value.id)] : []))
// Said before saving: plans this item is the last content of, and would leave.
const emptiedPlans = computed(() => (editing.value ? plansEmptiedBy(props.plans, refsForThis.value, form.value.access === 'paid' ? planIds.value : []) : []))
const emptiedTitles = computed(() => emptiedPlans.value.map((p) => `“${p.title}”`).join(' and '))

const toggleCategory = (id, on) => {
	const set = new Set(form.value.categoryIds.map(String))
	if (on) set.add(String(id))
	else set.delete(String(id))
	form.value.categoryIds = [...set].map((v) => (Number.isFinite(Number(v)) ? Number(v) : v))
}

const LIVE_TYPES = ['live', 'ipcam', 'subsecond', 'scheduled']

/**
 * Turn a StreamSourceDropdown pick into a castr ref.
 *   live     → { type: 'live', id: <stream id> }
 *   vod file → { type: 'vod', id: <folder stream id>, fileId: <playlist-video meta id> }
 *              (Live-to-VOD recordings are files inside a folder → same ref)
 *   playlist → { type: 'playlist', id }
 * With `source="sites"` the picker hands a VOD file over as `{ file, folder }`
 * because the raw file object carries no reference to its folder. `kind` is
 * the picker tab the pick came from (livestream | vod | playlist).
 */
function toCastrRef(source, kind) {
	if (!source) return null
	if (source.file && source.folder) {
		const folderId = source.folder._id || source.folder.id
		const fileId = source.file._id || source.file.id
		return folderId && fileId ? { type: 'vod', id: String(folderId), fileId: String(fileId) } : null
	}
	// Shapes other pickers / a stored item hand over: file with an explicit parent.
	if (source.isPlaylistVideo || (source.parent && (source.type === 'vod-file' || source.fileName))) {
		return { type: 'vod', id: String(source.parent), fileId: String(source._id || source.id) }
	}
	if (kind === 'playlist' || (Array.isArray(source.streams) && !source.key)) return { type: 'playlist', id: String(source._id) }
	if (source.type === 'vod') return null // a folder is not playable on its own; pick a file inside it
	if (kind === 'livestream' || LIVE_TYPES.includes(source.type)) return { type: 'live', id: String(source._id) }
	// A bare VOD file without its folder (legacy picker shape) cannot be resolved.
	if (source.fileName && !source.streams) return null
	return { type: 'live', id: String(source._id) }
}

/** Shape the dropdown understands for its label / icon (a single VOD file). */
function displaySource(source) {
	if (source?.file && source?.folder) return { ...source.file, vodFile: source.file, folder: source.folder }
	return source
}

const playbackLabel = computed(() => {
	if (!resolved.value) return ''
	if (resolved.value.playback_type === 'embed') return 'Castr player embed'
	if (resolved.value.playback_type === 'hls') return 'HLS'
	return resolved.value.playback_type ? String(resolved.value.playback_type).toUpperCase() : 'Playback'
})

const onSourceSelected = async (source, kind) => {
	sourceTab.value = 'castr'
	castrSource.value = displaySource(source)
	const ref = toCastrRef(source, kind)
	resolved.value = null
	if (!ref) {
		form.value.castr_ref = null
		dropPrefill()
		error.value = 'Pick a single video inside a folder, a live stream or a playlist.'
		return
	}
	error.value = null
	errorLinksFeatured.value = false
	form.value.castr_ref = ref
	resolving.value = true
	try {
		const res = await SiteAdminService.resolveCastrPlayback(props.siteId, ref)
		// castr may normalise the ref (e.g. swap a playlist-video meta id for the file id)
		if (res?.castr_ref?.type === ref.type && res.castr_ref.id) form.value.castr_ref = res.castr_ref
		resolved.value = res
		if (!form.value.title.trim() && res?.title) form.value.title = res.title
		applyPrefill(res)
	} catch (err) {
		error.value = err.message || 'Could not resolve this Castr source.'
		form.value.castr_ref = null
		dropPrefill()
	}
	resolving.value = false
}

// --- Steps & preview --------------------------------------------------------
const STEPS = [
	{ id: 'source', label: 'Source' },
	{ id: 'details', label: 'Details' },
	{ id: 'access', label: 'Access' },
	{ id: 'schedule', label: 'Schedule' },
]
const bodyEl = ref(null)
const stepDone = computed(() => ({
	source: hasSource.value,
	details: form.value.title.trim().length > 0,
	access: accessReady.value,
	schedule: !scheduleError.value && !availabilityError.value,
}))
const goStep = (id) => {
	const el = bodyEl.value?.querySelector?.(`[data-step="${id}"]`)
	if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
const previewKind = computed(() => {
	if (sourceTab.value === 'castr') return form.value.castr_ref?.type || 'vod'
	return /\.m3u8(\?|$)/i.test(form.value.external_url) ? 'live' : 'vod'
})
const previewPlans = computed(() => {
	const selected = new Set(planIds.value.map(String))
	return (props.plans || []).filter((p) => p.scope === 'site' || selected.has(String(p.public_id)))
})
const previewCategories = computed(() => (props.broadcast ? [] : selectedCategories.value.map((c) => c.name).filter(Boolean)))

// Broadcast stage: the countdown follows the earliest of the main stream's
// schedule, its Calendar entries and the next service time. Warn (never block)
// when the schedule set here is not the one viewers will count down to.
const isMainStreamCandidate = computed(() => {
	if (!props.broadcast || previewKind.value !== 'live') return false
	const main = Number(props.broadcast.liveItemId) || 0
	return main === 0 || (Boolean(editing.value) && String(main) === String(editing.value.id))
})
const broadcastNotice = computed(() => {
	if (!isMainStreamCandidate.value || !scheduledStartIso.value) return ''
	const managedId = form.value.calendar.eventId
	return broadcastConflictMessage(broadcastScheduleConflict({
		scheduledStart: scheduledStartIso.value,
		serviceTimes: props.broadcast.serviceTimes || [],
		timeZone: props.broadcast.timeZone || 'UTC',
		otherEntryStarts: itemEvents.value.filter((e) => e.id !== managedId).map((e) => e.start),
	}))
})
// How viewers will label this start: a service, or a one-off "Next broadcast".
const serviceNotice = computed(() => {
	if (!props.broadcast || !scheduledStartIso.value) return null
	if (previewKind.value !== 'live' && !form.value.calendar.enabled) return null
	return serviceTimeNotice({ start: scheduledStartIso.value, serviceTimes: props.broadcast.serviceTimes || [], timeZone: props.broadcast.timeZone || 'UTC' })
})

/**
 * @param {object|null} item        existing item to edit, null to create
 * @param {string[]} currentPlanIds plans the item is mapped into today
 * @param {{ scheduledStart?: string, calendar?: boolean, focus?: string }} [opts]
 *   `scheduledStart` (ISO) prefills the schedule of a new item — the Calendar
 *   opens "Add content" on a clicked day this way; `focus` scrolls to a step.
 */
const show = (item = null, currentPlanIds = [], opts = {}) => {
	editing.value = item
	error.value = null
	errorLinksFeatured.value = false
	resolved.value = null
	castrSource.value = null
	prefilled.value = null
	itemEvents.value = []
	planIds.value = [...currentPlanIds]
	// An existing paid item mapped into no per-item plan is, by definition, "all-access only".
	allAccessOnly.value = Boolean(item) && (item.access || 'public') === 'paid' && currentPlanIds.length === 0
	if (item) {
		form.value = {
			title: item.title || '',
			description: item.description || '',
			// A snapshot saved by an older castr is shown as "no poster": saving
			// then sends `poster: null` + the ref and castr clears it upstream.
			poster: item.poster && !isSnapshotUrl(item.poster) ? item.poster : '',
			categoryIds: [...(item.categoryIds || [])],
			access: item.access || 'public',
			published: item.published !== false,
			autoplay: item.autoplay === true,
			castr_ref: item.castr_ref || null,
			external_url: item.external_url || '',
			scheduledStart: item.countdown?.enabled ? toLocalInput(item.countdown.startsAt) : '',
			availableFrom: toLocalInput(item.availability?.from),
			availableUntil: toLocalInput(item.availability?.until),
			calendar: { enabled: false, durationMinutes: 60, eventId: null },
		}
		sourceTab.value = item.castr_ref ? 'castr' : (item.external_url ? 'external' : 'castr')
		if (item.castr_ref) previewLinkedSource(item.castr_ref)
		loadCalendarEvent(item)
	} else {
		form.value = blank()
		form.value.autoplay = props.playerOpenImmediately !== false
		sourceTab.value = 'castr'
		if (opts.scheduledStart) {
			form.value.scheduledStart = toLocalInput(opts.scheduledStart)
			form.value.calendar.enabled = opts.calendar !== false
		}
	}
	isVisible.value = true
	if (opts.focus) setTimeout(() => goStep(opts.focus), 250)
}
const close = () => { isVisible.value = false }

// Editing a linked item: fetch the current Castr playback URL / thumbnail in
// the background; an item without a poster gets the Castr image prefilled.
async function previewLinkedSource(ref) {
	try {
		const res = await SiteAdminService.resolveCastrPlayback(props.siteId, ref)
		if (isVisible.value && !castrSource.value && form.value.castr_ref === ref) {
			resolved.value = res
			applyPrefill(res)
		}
	} catch {
		// preview only — the stored item is still editable without it
	}
}
watch(isVisible, (open) => { if (!open) editing.value = null })

/**
 * A plan created from the "+ New plan" button while this modal is open: tick
 * it so the item is mapped into it on save (for a new item the id only exists
 * after the POST, so the mapping cannot be part of the plan itself).
 */
const selectPlan = (product) => {
	if (!isVisible.value || !product?.public_id || product.scope !== 'items') return
	if (!planIds.value.includes(product.public_id)) planIds.value = [...planIds.value, product.public_id]
	allAccessOnly.value = false
}

const save = async () => {
	if (!canSubmit.value) return
	saving.value = true
	error.value = null
	errorLinksFeatured.value = false
	// Plans travel with the access change: the runtime mirrors `planIds`
	// (cleared for non-paid), castr's PUT …/plans/{id}/items follows in `saved`.
	const chosenPlanIds = form.value.access === 'paid' ? planIds.value : []
	const payload = {
		title: form.value.title.trim(),
		description: form.value.description.trim() || null,
		poster: form.value.poster || null,
		categoryIds: form.value.categoryIds,
		access: form.value.access,
		planIds: chosenPlanIds,
		published: form.value.published,
		autoplay: form.value.autoplay === true,
		// An empty start switches the countdown off; an empty window clears both bounds.
		countdown: { enabled: Boolean(scheduledStartIso.value), startsAt: scheduledStartIso.value },
		availability: { from: availableFromIso.value, until: availableUntilIso.value },
	}
	if (sourceTab.value === 'castr') {
		// castr resolves playback_url / external_url from the ref server-side and,
		// when the poster is left empty, fills it from the Castr VOD thumbnail
		// (never a live snapshot) — so the ref is also re-sent when the owner
		// cleared the poster on an existing item.
		if (form.value.castr_ref && (!editing.value || castrSource.value || !payload.poster)) payload.castr_ref = form.value.castr_ref
	}
	if (!editing.value) payload.type = form.value.castr_ref?.type === 'live' ? 'live' : 'vod'
	try {
		const res = editing.value
			? await SiteAdminService.updateContent(props.siteId, editing.value.id, payload)
			: await SiteAdminService.createContent(props.siteId, payload)
		const item = res?.item || res
		if (!editing.value) {
			trackSites(SITES_EVENTS.CONTENT_ADDED, props.siteId, { type: form.value.castr_ref?.type || 'external', access: payload.access })
		} else if ((editing.value.access || 'public') !== payload.access) {
			trackSites(SITES_EVENTS.CONTENT_ACCESS_CHANGED, props.siteId, { from: editing.value.access || 'public', to: payload.access, items: 1 })
		}
		const calendarProblem = await syncCalendarEvent(item, payload.title)
		// Sites outlet: a plan notice from the parent's mapping sync may follow and stacks under this one.
		if (calendarProblem) notifySite({ type: 'error', text: `Content saved, but the calendar entry could not be updated (${calendarProblem}). Check the Calendar tab.` })
		else notifySite({ type: 'success', text: editing.value ? 'Content updated.' : 'Content added.' })
		emit('saved', { item, planIds: chosenPlanIds })
		isVisible.value = false
	} catch (err) {
		const slides = featuredSourceSlides(err)
		errorLinksFeatured.value = Boolean(slides)
		if (slides) error.value = featuredSourceMessage(slides)
		else if (err.reason === 'stripe_not_connected') error.value = 'Paid access needs a connected Stripe account. Connect Stripe under "Who can watch this item", or pick another access level.'
		else error.value = err.message || 'Could not save this content.'
	}
	saving.value = false
}

defineExpose({ show, close, selectPlan })
</script>

<template>
	<ModalBox v-model="isVisible" :title="editing ? 'Edit content' : 'Add content'" placement="right" size="xl" scroll-body>
		<div ref="bodyEl" class="flex flex-col gap-4 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="content-drawer">
			<!-- Step nav -->
			<nav class="sticky top-0 z-10 -mx-4 -mt-2 flex items-center gap-1 overflow-x-auto bg-surface-2 px-4 py-2 sm:-mx-6 sm:px-6" aria-label="Content steps" data-testid="content-steps">
				<button
					v-for="(step, i) in STEPS"
					:key="step.id"
					type="button"
					class="flex h-7 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-tiny font-medium text-surface-8 transition-colors hover:bg-surface-3 hover:text-white"
					:data-testid="`content-step-${step.id}`"
					:data-done="stepDone[step.id] ? 'true' : 'false'"
					@click="goStep(step.id)"
				>
					<span class="grid size-4 place-items-center rounded-full text-[10px] font-semibold" :class="stepDone[step.id] ? 'bg-success text-black' : 'bg-surface-4 text-surface-8'">
						<template v-if="stepDone[step.id]">✓</template>
						<template v-else>{{ i + 1 }}</template>
					</span>
					{{ step.label }}
				</button>
			</nav>

			<Alert v-if="error" type="error" class="!mb-0" data-testid="content-error">
				<span>
					{{ error }}
					<router-link v-if="errorLinksFeatured" :to="{ name: 'SiteFeatured', params: { siteId } }" class="underline" data-testid="featured-source-link">Open Featured</router-link>
				</span>
			</Alert>

			<div class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_16rem]">
				<form class="flex min-w-0 flex-col gap-6" @submit.prevent="save">
					<!-- 1 · Source -->
					<section data-step="source" class="scroll-mt-14">
						<h3 class="mb-2 flex items-center gap-2 text-sm font-semibold text-white"><span class="text-surface-6">1</span> Source</h3>
						<div v-if="sourceTab === 'external'" class="mb-3 min-w-0 rounded-md border border-surface-4 bg-surface-3 px-3 py-2.5" data-testid="content-legacy-external">
							<p class="mb-1 text-tiny text-surface-8">This item uses an external link, which is no longer offered. Pick a Castr stream or video to replace it.</p>
							<p class="mb-0 truncate text-tiny text-surface-7" :title="form.external_url" data-testid="content-legacy-external-url">{{ form.external_url }}</p>
						</div>
						<StreamSourceDropdown :selected-stream="castrSource" source="sites" :show-playlist="true" close-after-select
							placeholder="Choose a live stream, video or playlist" @on-source-selected="onSourceSelected" />
						<p v-if="resolving" class="mb-0 mt-2 text-tiny text-surface-7">Resolving playback…</p>
						<div v-else-if="resolved" class="mt-2 flex min-w-0 items-center gap-2 text-tiny text-surface-7" data-testid="castr-playback-preview">
							<span class="shrink-0 rounded bg-surface-3 px-1.5 py-0.5 font-medium uppercase text-surface-8">{{ playbackLabel }}</span>
							<a v-if="resolved.playback_url" :href="resolved.playback_url" target="_blank" rel="noopener" class="min-w-0 truncate text-surface-7 hover:text-white" :title="resolved.playback_url">{{ resolved.playback_url }}</a>
							<span v-else class="min-w-0 truncate">Playback ready</span>
						</div>
						<p v-else-if="editing && form.castr_ref" class="mb-0 mt-2 text-tiny text-surface-7">
							Linked to a Castr {{ castrSourceNoun(form.castr_ref) }} · pick a new source to change it.
						</p>
						<p v-else-if="sourceTab === 'castr'" class="mb-0 mt-2 text-tiny text-surface-7">Castr resolves the playback URL for you and keeps it updated.</p>
					</section>

					<!-- 2 · Details -->
					<section data-step="details" class="scroll-mt-14 flex flex-col gap-4">
						<h3 class="mb-0 flex items-center gap-2 text-sm font-semibold text-white"><span class="text-surface-6">2</span> Details</h3>
						<FormInput id="content-title" v-model="form.title" label="Title" placeholder="e.g. Main stage" input-classes="placeholder:text-surface-6" :maxlength="255" />
						<FormTextarea id="content-description" v-model="form.description" label="Description (optional)" placeholder="e.g. Shown on the item page — what viewers will see" />
						<div>
							<SiteImageField id="content-poster" v-model="form.poster" :site-id="siteId" label="Thumbnail"
								hint="Recommended 1280×720 JPG/PNG, ≤ 2 MB" :recommended-max-bytes="2 * 1024 * 1024" />
							<p v-if="posterNote" class="mb-0 mt-1 text-tiny text-surface-7" data-testid="poster-note">{{ posterNote }}</p>
						</div>
						<div v-if="!broadcast" data-testid="content-categories">
							<p class="mb-2 text-tiny font-medium text-surface-text-1">Categories</p>
							<div v-if="categories.length" class="flex flex-wrap gap-2">
								<label v-for="cat in categories" :key="cat.id" class="flex items-center gap-2 rounded-lg bg-surface-3 px-3 py-1.5 text-sm text-surface-text-1">
									<FormCheckbox :name="`content-cat-${cat.id}`" :model-value="form.categoryIds.map(String).includes(String(cat.id))"
										@update:model-value="toggleCategory(cat.id, $event)" />
									<span>{{ cat.name }}</span>
								</label>
							</div>
							<p v-else class="mb-0 text-tiny text-surface-7">No categories yet — add some under Content › Categories.</p>
						</div>
						<div class="rounded-lg bg-surface-3 px-3 py-2.5">
							<FormSwitch id="content-autoplay" v-model="form.autoplay" label="Open the player immediately for this item"
								label-position="left" label-styles="w-full justify-between gap-3 text-sm text-surface-text-1" />
							<p class="mb-0 mt-0.5 pr-14 text-tiny text-surface-7" data-testid="content-autoplay-help">Follows the site setting by default; the item setting always wins. On, viewers start on the player instead of the artwork hero with a Play button. Locked items still show the hero.</p>
						</div>
						<div class="rounded-lg bg-surface-3 px-3 py-2.5">
							<FormSwitch id="content-published" v-model="form.published" :label="form.published ? 'Visible on the site' : 'Hidden (draft)'"
								label-position="left" label-styles="w-full justify-between gap-3 text-sm text-surface-text-1" />
							<p class="mb-0 mt-0.5 pr-14 text-tiny text-surface-7">Visibility applies as soon as you save — it is not part of the theme draft.</p>
						</div>
					</section>

					<!-- 3 · Access -->
					<section data-step="access" class="scroll-mt-14">
						<h3 class="mb-2 flex items-center gap-2 text-sm font-semibold text-white">
							<span class="text-surface-6">3</span> Who can watch this item
						</h3>
						<SiteAccessSelect v-model="form.access" v-model:plan-ids="planIds" v-model:all-access-only="allAccessOnly" :products="plans" :refs="refsForThis"
							:can-monetize="canMonetize" :stripe="stripe" :pending="!editing"
							@create-plan="emit('create-plan', $event)" @stripe-connected="emit('stripe-connected')" />
						<p v-if="form.access === 'paid' && !editing" class="mb-0 mt-2 text-tiny text-surface-7">
							Selected plans are linked to this item right after it is created.
						</p>
						<p v-if="clearsMapping" class="mb-0 mt-2 rounded-md bg-surface-3 px-3 py-2 text-tiny text-surface-8" data-testid="access-clear-note">
							This item is paid today. Saving it as {{ form.access === 'registered' ? 'Login required' : 'Public' }} clears its plan mapping — it is removed from every plan that unlocked it.
						</p>
						<p v-if="emptiedPlans.length" class="mb-0 mt-2 rounded-md bg-surface-3 px-3 py-2 text-tiny text-surface-8" data-testid="access-empties-note">
							{{ emptiedTitles }} would then unlock nothing — edit or archive {{ emptiedPlans.length === 1 ? 'it' : 'them' }} under Plans afterwards.
						</p>
					</section>

					<!-- 4 · Schedule -->
					<section data-step="schedule" class="scroll-mt-14" data-testid="schedule-section">
						<h3 class="mb-2 flex items-center gap-2 text-sm font-semibold text-white">
							<span class="text-surface-6">4</span> Schedule
							<span v-if="timeZone" class="ml-auto text-tiny font-normal text-surface-7">times in {{ timeZone }}</span>
						</h3>
						<div class="flex flex-col gap-3 rounded-lg bg-surface-3 px-3 py-3">
							<div>
								<FormInput id="content-scheduled-start" v-model="form.scheduledStart" type="datetime-local" label="Scheduled start (optional)"
									input-classes="[color-scheme:dark] !bg-surface-4" :is-error="Boolean(scheduleError)" />
								<p class="mb-0 mt-1.5 text-tiny" :class="scheduleError ? 'text-error' : 'text-surface-7'">
									{{ scheduleError || 'Until then the item shows an “Upcoming” badge with a countdown instead of the player. Clear the field to remove the countdown.' }}
								</p>
								<Alert v-if="broadcastNotice" type="warning" class="!mb-0 mt-2" data-testid="broadcast-schedule-notice">
									<span>{{ broadcastNotice }}</span>
								</Alert>
								<Alert v-if="serviceNotice" class="!mb-0 mt-2" data-testid="broadcast-service-match-notice" :data-kind="serviceNotice.kind">
									<span v-if="serviceNotice.kind === 'none'">Add service times in <router-link :to="{ name: 'SiteBroadcast', params: { siteId } }" class="underline" data-testid="broadcast-service-times-link">Settings › Broadcast</router-link> so viewers know your regular schedule.</span>
									<span v-else>{{ serviceNotice.text }}</span>
								</Alert>
							</div>
							<div>
								<div class="grid gap-3 sm:grid-cols-2">
									<FormInput id="content-available-from" v-model="form.availableFrom" type="datetime-local" label="Available from (optional)"
										input-classes="[color-scheme:dark] !bg-surface-4" :is-error="Boolean(availabilityError)" />
									<FormInput id="content-available-until" v-model="form.availableUntil" type="datetime-local" label="Available until (optional)"
										input-classes="[color-scheme:dark] !bg-surface-4" :is-error="Boolean(availabilityError)" />
								</div>
								<p class="mb-0 mt-1 text-tiny" :class="availabilityError ? 'text-error' : 'text-surface-7'" data-testid="availability-note">
									{{ availabilityError || 'Outside this window the item is hidden from the site, including the calendar. Set both or leave both empty.' }}
								</p>
							</div>
							<div class="border-t border-surface-4 pt-3">
								<FormCheckbox name="content-calendar" :input-value="true" size="md" label="Show on the site calendar"
									:model-value="form.calendar.enabled" :disabled="!scheduledStartIso" @update:model-value="form.calendar.enabled = $event" />
								<div v-if="form.calendar.enabled" class="mt-2 flex items-center gap-2">
									<label for="content-calendar-duration" class="mb-0 shrink-0 text-tiny text-surface-8">Duration</label>
									<FormSelect id="content-calendar-duration" v-model="form.calendar.durationMinutes" :options="DURATION_OPTIONS" size="sm" class="!w-44" />
								</div>
								<p class="mb-0 mt-1.5 text-tiny text-surface-7" data-testid="calendar-note">
									Your site's <span class="font-medium text-surface-8">/calendar</span> page lists these entries with the item's artwork and a live / upcoming state.
									<template v-if="calendarNote"> {{ calendarNote }}</template>
								</p>
							</div>
						</div>
					</section>
				</form>

				<!-- Live card preview -->
				<aside class="xl:sticky xl:top-12 xl:self-start" data-testid="content-preview-pane">
					<p class="mb-2 text-tiny font-medium uppercase tracking-wider text-surface-6">Preview</p>
					<SiteContentCardPreview
						:title="form.title"
						:description="form.description"
						:poster="form.poster"
						:kind="previewKind"
						:access="form.access"
						:published="form.published"
						:scheduled-start="scheduledStartIso || ''"
						:categories="previewCategories"
						:plans="previewPlans"
						:paid-label="paidBadgeLabel"
						:broadcast="!!broadcast"
					/>
					<p class="mb-0 mt-2 text-tiny text-surface-7">How the card looks in the site's library. The exact size, font and badge text follow your theme.</p>
				</aside>
			</div>
		</div>

		<template #footer>
			<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:items-center sm:p-6">
				<FormButton class="w-full justify-center sm:w-auto" :disabled="!canSubmit" :is-loading="saving" @click="save">
					{{ editing ? 'Save changes' : 'Add content' }}
				</FormButton>
				<FormButton class="w-full justify-center sm:w-auto" type="secondary" @click="close">Cancel</FormButton>
				<p v-if="!canSubmit && !saving" class="mb-0 text-tiny text-surface-7 sm:mr-auto" data-testid="content-submit-hint">
					<template v-if="!hasSource">Pick a source to continue.</template>
					<template v-else-if="!form.title.trim()">Give the item a title.</template>
					<template v-else-if="!accessReady">Choose the plans that unlock this paid item.</template>
					<template v-else-if="scheduleError || availabilityError">Fix the schedule to continue.</template>
				</p>
			</footer>
		</template>
	</ModalBox>
</template>
